import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { isDispatchable } from "@/lib/production";
import { toast } from "sonner";
import { Eye, Loader2, PackageSearch } from "lucide-react";

const num = (x) => Number(x) || 0;

export default function DispatchPreview() {
  const { data: orderData, isLoading, isError, error, refetch } = useResource("/dispatch/orders");
  const { data: fgData } = useResource("/finished-goods");
  const orders = useMemo(() => (Array.isArray(orderData) ? orderData : []), [orderData]);
  const fgMap = useMemo(() => {
    const m = {};
    (Array.isArray(fgData) ? fgData : []).forEach((r) => {
      const current = num(r.total ?? r.current ?? r.currentStock);
      m[r.sku] = { current, reserved: num(r.reserved), available: r.available !== undefined ? num(r.available) : current - num(r.reserved), status: r.status };
    });
    return m;
  }, [fgData]);

  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reqQty, setReqQty] = useState({}); // sku -> simulated qty

  const loadPreview = async (o) => {
    setSelected(o); setPreview(null); setLoading(true);
    try {
      const res = await api.get(`/dispatch/${o.id || o.orderId}`);
      setPreview(res.data);
      const lines = res.data?.lines || res.data?.items || [];
      const init = {}; lines.forEach((l) => { init[l.sku] = num(l.dispatchQty ?? l.qty ?? l.orderedQty); });
      setReqQty(init);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not load preview")); }
    finally { setLoading(false); }
  };

  const previewLines = preview?.lines || preview?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader title="Dispatch Preview" subtitle="Simulate a dispatch and see the resulting FG movement — without touching stock." />

      <div className="flex items-center gap-2 rounded-lg border-2 border-dashed border-rose-300 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700" data-testid="dispatch-preview-banner">
        <Eye className="h-4 w-4" /> PREVIEW — NO STOCK MOVEMENT. This is a calculation only; nothing is dispatched, deducted or posted.
      </div>

      {isLoading ? <LoadingState label="Loading dispatch-ready orders…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Orders list */}
            <div className="lg:col-span-1">
              <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
                <div className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-sm font-semibold text-stone-800">Dispatch-ready orders</div>
                {orders.length === 0 ? (
                  <div className="p-6"><EmptyState title="No orders ready" message="Orders marked ready for dispatch will appear here." /></div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {orders.map((o) => (
                      <button key={o.id || o.orderId} data-testid={`dispatch-order-${o.id || o.orderId}`} onClick={() => loadPreview(o)}
                        className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-amber-50/40 ${selected?.id === o.id ? "bg-amber-50" : ""}`}>
                        <div>
                          <div className="text-sm font-medium text-stone-800">{o.orderNumber || o.orderNo || (o.id || "").slice(0, 8)}</div>
                          <div className="text-xs text-stone-500">{o.buyerName || o.buyer || "—"}</div>
                        </div>
                        <StatusBadge value={o.status} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview panel */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                {!selected ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-sm text-stone-400">
                    <PackageSearch className="mb-2 h-8 w-8" /> Select an order to preview its dispatch impact.
                  </div>
                ) : loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div> : (
                  <>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="font-display text-base font-semibold text-stone-900">{selected.orderNumber || selected.orderNo} · {selected.buyerName || "—"}</h3>
                      <StatusBadge value={selected.status} />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b border-stone-200 text-left text-[11px] font-bold uppercase text-stone-500">
                          <tr>
                            <th className="py-2 pr-2">Product / SKU</th><th className="py-2 px-2">Batch</th>
                            <th className="py-2 px-2 text-right">Current FG</th><th className="py-2 px-2 text-right">Reserved</th>
                            <th className="py-2 px-2 text-right">Available</th><th className="py-2 px-2 text-right">Dispatch Qty</th>
                            <th className="py-2 pl-2 text-right">Proposed Remaining</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                          {previewLines.length === 0 ? (
                            <tr><td colSpan={7} className="py-6 text-center text-stone-400">No dispatch lines on this order.</td></tr>
                          ) : previewLines.map((l, i) => {
                            const fg = fgMap[l.sku] || { current: num(l.currentFg), reserved: num(l.reserved), available: num(l.availableFg ?? l.available), status: l.status };
                            const qty = reqQty[l.sku] ?? num(l.dispatchQty ?? l.qty);
                            const remaining = fg.current - qty;
                            const short = qty > fg.available;
                            return (
                              <tr key={i} data-testid={`dispatch-line-${l.sku || i}`}>
                                <td className="py-2 pr-2"><div className="text-stone-800">{l.productName || l.name}</div><div className="font-mono text-[11px] text-stone-400">{l.sku}</div></td>
                                <td className="py-2 px-2 font-mono text-xs text-stone-500">{l.batchNo || "—"}</td>
                                <td className="py-2 px-2 text-right font-mono">{fmtNumber(fg.current)}</td>
                                <td className="py-2 px-2 text-right font-mono text-amber-700">{fmtNumber(fg.reserved)}</td>
                                <td className={`py-2 px-2 text-right font-mono ${isDispatchable(fg.status) ? "text-emerald-700" : "text-stone-400"}`}>{fmtNumber(fg.available)}</td>
                                <td className="py-2 px-2 text-right">
                                  <Input data-testid={`dispatch-qty-${l.sku || i}`} type="number" value={qty}
                                    onChange={(e) => setReqQty((s) => ({ ...s, [l.sku]: Number(e.target.value) }))}
                                    className={`h-8 w-24 text-right font-mono text-sm ${short ? "border-rose-300 text-rose-600" : ""}`} />
                                </td>
                                <td className={`py-2 pl-2 text-right font-mono font-semibold ${remaining < 0 ? "text-rose-600" : "text-stone-900"}`}>{fmtNumber(remaining)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-3 text-xs text-stone-400">Editable "Dispatch Qty" is for simulation only. Actual dispatch and FG deduction happen in the Dispatch module after explicit authorization.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
